import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import { SchoolYear, CrudPermissions } from '../../types';
import { useData } from '../../contexts/DataContext';

interface SchoolYearsManagementProps {
  permissions: CrudPermissions;
}
import { v4 as uuidv4 } from 'uuid';

export const SchoolYearsManagement: React.FC<SchoolYearsManagementProps> = ({ permissions }) => {
  const { schoolYears, setSchoolYears } = useData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingYear, setEditingYear] = useState<SchoolYear | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<Partial<SchoolYear>>({
    name: '',
    startDate: new Date(),
    endDate: new Date(),
    isActive: false,
    schoolId: '750e8400-e29b-41d4-a716-446655440001',
  });

  const filteredYears = useMemo(() => {
    return schoolYears.filter((year) => {
      const matchesSearch = year.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [schoolYears, searchQuery]);

  const handleAdd = () => {
    setEditingYear(null);
    setFormData({
      name: '',
      startDate: new Date(),
      endDate: new Date(),
      isActive: false,
      schoolId: '750e8400-e29b-41d4-a716-446655440001',
    });
    setOpenDialog(true);
  };

  const handleEdit = (year: SchoolYear) => {
    setEditingYear(year);
    setFormData(year);
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this school year?')) {
      setSchoolYears(schoolYears.filter((y) => y.id !== id));
      const index = mockSchoolYears.findIndex((y) => y.id === id);
      if (index > -1) {
        mockSchoolYears.splice(index, 1);
      }
    }
  };

  const handleSave = () => {
    if (!formData.name || !formData.startDate || !formData.endDate) {
      alert('Please fill in all required fields');
      return;
    }

    if (editingYear) {
      const updated = schoolYears.map((y) =>
        y.id === editingYear.id ? { ...y, ...formData } : y
      );
      setSchoolYears(updated);
      const index = mockSchoolYears.findIndex((y) => y.id === editingYear.id);
      if (index > -1) {
        mockSchoolYears[index] = { ...mockSchoolYears[index], ...formData } as SchoolYear;
      }
    } else {
      const newYear: SchoolYear = {
        id: uuidv4(),
        name: formData.name!,
        startDate: formData.startDate!,
        endDate: formData.endDate!,
        isActive: formData.isActive!,
        schoolId: '750e8400-e29b-41d4-a716-446655440001',
      };
      setSchoolYears([...schoolYears, newYear]);
      mockSchoolYears.push(newYear);
    }
    setOpenDialog(false);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            School Years Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage academic years for your school
          </Typography>
        </Box>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Add School Year
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search school years..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(auto-fill, minmax(320px, 1fr))',
          },
          gap: { xs: 2, sm: 2 },
        }}
      >
        {filteredYears.map((year) => (
          <Box key={year.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Box
                    sx={{
                      bgcolor: '#1e293b',
                      borderRadius: '8px',
                      p: 1,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CalendarMonth sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                      {year.name}
                    </Typography>
                    <Chip
                      label={year.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={year.isActive ? 'success' : 'default'}
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Start Date
                    </Typography>
                    <Typography variant="body2">{formatDate(year.startDate)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      End Date
                    </Typography>
                    <Typography variant="body2">{formatDate(year.endDate)}</Typography>
                  </Box>
                </Box>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                {permissions.update && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleEdit(year)}
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    Edit
                  </Button>
                )}
                {permissions.delete && (
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(year.id)}
                    color="error"
                    sx={{ flexShrink: 0 }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>

      {filteredYears.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No school years found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? 'Try adjusting your search'
              : 'Click "Add School Year" to create your first school year'}
          </Typography>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingYear ? 'Edit School Year' : 'Add New School Year'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="School Year Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              helperText="e.g., 2024-2025, Summer 2025"
            />
            <TextField
              label="Start Date"
              type="date"
              value={formData.startDate instanceof Date ? formData.startDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setFormData({ ...formData, startDate: new Date(e.target.value) })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="End Date"
              type="date"
              value={formData.endDate instanceof Date ? formData.endDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setFormData({ ...formData, endDate: new Date(e.target.value) })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive ?? false}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
            }}
          >
            {editingYear ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
